/**
 * MySharesPanel - Panel del usuario para gestionar sus snapshots compartidos.
 * Endpoints:
 *  - GET  /api/share/mine
 *  - PATCH /api/share/{id}  (editar título)
 *  - DELETE /api/share/{id}
 *
 * Se abre como modal desde el menú o desde Settings. Muestra tabla con:
 * título editable, herramienta, fecha de creación, vistas, estado (activo/caducado),
 * botones: abrir, copiar link, editar título, eliminar.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Eye, CalendarBlank, Trash, PencilSimple, Copy, ArrowSquareOut, ShareNetwork, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOOL_META = {
  comparator: { icon: '📊', label: 'Comparador', color: '#06b6d4' },
  proanalyst: { icon: '🩺', label: 'Pro-Analyst', color: '#fbbf24' },
  analyzer: { icon: '📈', label: 'Análisis', color: '#8b5cf6' },
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
};

export const MySharesPanel = ({ open, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/share/mine`, { timeout: 12000, withCredentials: true });
      setItems(res.data?.shares || []);
    } catch (e) {
      toast.error('No se pudieron cargar tus enlaces');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const copyLink = async (id) => {
    const url = `${window.location.origin}/share/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const openLink = (id) => {
    window.open(`/share/${id}`, '_blank', 'noopener');
  };

  const startEdit = (item) => {
    setEditingId(item.share_id);
    setEditValue(item.title || '');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const title = editValue.trim();
    if (!title) { toast.error('Título vacío'); return; }
    try {
      await axios.patch(`${API_URL}/api/share/${editingId}`, { title }, { timeout: 8000, withCredentials: true });
      setItems(prev => prev.map(it => it.share_id === editingId ? { ...it, title } : it));
      toast.success('Título actualizado');
      setEditingId(null);
    } catch (e) {
      toast.error('No se pudo guardar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este enlace? Los que lo tengan copiado dejarán de verlo.')) return;
    try {
      await axios.delete(`${API_URL}/api/share/${id}`, { timeout: 8000, withCredentials: true });
      setItems(prev => prev.filter(it => it.share_id !== id));
      toast.success('Enlace eliminado');
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="myshares-modal-overlay"
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] bg-[#0f172a] border border-gold-500/30 rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <ShareNetwork size={20} className="text-gold-400" />
            Mis Compartidos
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white"
            data-testid="myshares-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="py-10 text-center">
              <div className="w-8 h-8 border-3 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-gray-500 mt-3">Cargando…</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center">
              <ShareNetwork size={48} className="text-gray-700 mx-auto mb-3" />
              <p className="text-white font-semibold">Aún no has compartido nada</p>
              <p className="text-xs text-gray-500 mt-1">Usa el botón «Compartir» en el Comparador, Pro-Analyst o Analizador para crear tu primer enlace.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="myshares-list">
              {items.map((it) => {
                const meta = TOOL_META[it.tool] || { icon: '🔗', label: it.tool, color: '#94a3b8' };
                const isEditing = editingId === it.share_id;
                return (
                  <div
                    key={it.share_id}
                    className={`p-3 rounded-xl border ${it.expired ? 'border-red-500/30 bg-red-500/5' : 'border-gray-800 bg-black/30 hover:bg-black/50'} transition-colors`}
                    data-testid={`myshares-item-${it.share_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icono tool */}
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                        style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}
                      >
                        {meta.icon}
                      </div>

                      {/* Info + edición */}
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                              autoFocus
                              className="flex-1 px-2 py-1 rounded bg-black/50 border border-gold-500 text-white text-sm focus:outline-none"
                              data-testid={`myshares-edit-input-${it.share_id}`}
                            />
                            <button
                              onClick={saveEdit}
                              className="px-2 py-1 rounded bg-gold-500 text-black text-xs font-bold hover:bg-gold-400"
                              data-testid={`myshares-save-${it.share_id}`}
                            >
                              Guardar
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-2 py-1 rounded bg-gray-800 text-gray-300 text-xs hover:bg-gray-700"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold text-sm truncate">{it.title || meta.label}</span>
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold" style={{ color: meta.color, background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}>{meta.label}</span>
                              {it.expired && (
                                <span className="text-[10px] font-bold text-red-400 flex items-center gap-1"><Warning size={10} weight="fill" />Caducado</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                              <span className="flex items-center gap-1"><CalendarBlank size={11} />{fmtDate(it.created_at)}</span>
                              <span className="flex items-center gap-1"><Eye size={11} />{it.views} vistas</span>
                              <span className="font-mono truncate hidden sm:inline">/{it.share_id}</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Acciones */}
                      {!isEditing && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!it.expired && (
                            <>
                              <button
                                onClick={() => openLink(it.share_id)}
                                className="p-1.5 rounded hover:bg-zinc-900/60 text-zinc-100 border border-gold-500/40"
                                title="Abrir en nueva pestaña"
                                data-testid={`myshares-open-${it.share_id}`}
                              >
                                <ArrowSquareOut size={14} />
                              </button>
                              <button
                                onClick={() => copyLink(it.share_id)}
                                className="p-1.5 rounded hover:bg-zinc-900/60 text-zinc-100 border border-gold-500/40"
                                title="Copiar link"
                                data-testid={`myshares-copy-${it.share_id}`}
                              >
                                <Copy size={14} />
                              </button>
                              <button
                                onClick={() => startEdit(it)}
                                className="p-1.5 rounded hover:bg-zinc-500/20 text-zinc-300"
                                title="Editar título"
                                data-testid={`myshares-edit-${it.share_id}`}
                              >
                                <PencilSimple size={14} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => remove(it.share_id)}
                            className="p-1.5 rounded hover:bg-red-500/20 text-red-400"
                            title="Eliminar"
                            data-testid={`myshares-delete-${it.share_id}`}
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 text-[11px] text-gray-500 text-center">
          Los enlaces caducan automáticamente a los <span className="text-gold-400 font-semibold">30 días</span>. Límite: 50 nuevos enlaces al día.
        </div>
      </div>
    </div>
  );
};

export default MySharesPanel;
